import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetAllCampaigns } from "@/entities/campaign/api/useGetAllCampaigns";
import { makeFormRequest } from "@/entities/campaign/model/lib/makeFormRequest";
import { useCampaignFormStore } from "@/entities/campaign/model/store/useCampaignFormStore";
import { CampaignLabel } from "@/entities/campaign/ui/CampaignLabel";
import { DATE } from "@/shared/model/const/services";
import { CreateOrCopyEntity } from "@/shared/ui/CreateOrCopyEntity";
import type { CreateOrCopyEntityPosition } from "@/shared/ui/CreateOrCopyEntity";

interface CreateOrCopyCampaignProps {
    children: ReactNode;
    dropdownWidth?: string;
    position?: CreateOrCopyEntityPosition;
    triggerClassName?: string;
    qaButton?: string;
}

export const CreateOrCopyCampaign = ({
    children,
    dropdownWidth,
    position = "rightBottom",
    triggerClassName,
    qaButton = "create-menu-button",
}: CreateOrCopyCampaignProps) => {
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const navigate = useNavigate();
    const flush = useCampaignFormStore((state) => state.flush);
    const setCampaignInfo = useCampaignFormStore((state) => state.setCampaignInfo);
    const { t } = useTranslation();
    const { data, isError, isLoading } = useGetAllCampaigns(
        { start_date: DATE.MIN, end_date: DATE.MAX },
        isCopyModalOpen,
    );

    const options = useMemo(
        () =>
            (data ?? [])
                .flatMap((campaign) =>
                    campaign.uuid
                        ? [
                              {
                                  campaign,
                                  key: campaign.uuid,
                                  label: <CampaignLabel campaign={campaign} />,
                                  searchText: `${campaign.name ?? ""} ${campaign.uuid}`,
                              },
                          ]
                        : [],
                )
                .sort((left, right) => left.searchText.localeCompare(right.searchText)),
        [data],
    );

    const createCampaign = () => {
        flush();
        navigate("/campaigns/create");
    };

    const copyCampaign = (campaign: (typeof options)[number]) => {
        flush();
        setCampaignInfo(makeFormRequest(campaign.campaign));
        navigate("/campaigns/create?step=1");
    };

    return (
        <CreateOrCopyEntity
            cancelLabel={t("regular.buttons.cancel")}
            confirmLabel={t("entity-copy.confirm")}
            copyLabel={t("regular.buttons.copy-campaign")}
            createLabel={t("regular.buttons.create-campaign")}
            description={t("campaigns-dashboard.duplicate-description")}
            dropdownWidth={dropdownWidth}
            emptyText={t("campaigns-dashboard.no-campaigns")}
            errorText={t("campaigns-dashboard.load-error")}
            isError={isError}
            isLoading={isLoading}
            modalTitle={t("campaigns-dashboard.select-existing-campaign")}
            onCopy={copyCampaign}
            onCopyModalOpenChange={setIsCopyModalOpen}
            onCreate={createCampaign}
            options={options}
            position={position}
            searchPlaceholder={t("entity-copy.search")}
            selectPlaceholder={t("regular.filters.placeholder")}
            triggerClassName={triggerClassName}
            qaTags={{
                button: qaButton,
                create: "create-campaign-option",
                copy: "copy-campaign-option",
                copyModal: "copy-campaign",
            }}
        >
            {children}
        </CreateOrCopyEntity>
    );
};
